import React, { useEffect, useState } from "react";
import { ChevronDown } from "lucide-react";
import { ICON_NAMES, DEFAULT_ICON } from "../lib/treatmentIcons";
import { DynamicIcon, isFontAwesome } from "../lib/dynamicIcon";

// Icon selector supporting BOTH built-in (lucide) icons and any free
// Font Awesome icon (admin pastes its class, e.g. "fa-solid fa-brain").
export const IconPicker = ({ value, onChange, testId }) => {
  const [open, setOpen] = useState(false);
  const [fa, setFa] = useState(isFontAwesome(value) ? value : "");

  // Keep the Font Awesome input in sync if the stored value changes elsewhere.
  useEffect(() => {
    setFa(isFontAwesome(value) ? value : "");
  }, [value]);

  const tid = testId || "icon-picker";
  const label = value || DEFAULT_ICON;

  const applyFa = () => {
    const cls = fa.trim();
    if (!cls) return;
    onChange(cls);
    setOpen(false);
  };

  return (
    <div className="relative">
      <button
        type="button"
        data-testid={`${tid}-btn`}
        onClick={() => setOpen((o) => !o)}
        className="inline-flex items-center gap-2 rounded-lg border border-cream-2 bg-white px-3 py-2 text-sm hover:border-sage transition-colors max-w-full"
      >
        <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-sage/12 text-sage-dark">
          <DynamicIcon name={value} className="h-4 w-4" faSize={16} />
        </span>
        <span className="text-ink-soft truncate max-w-[160px]">{label}</span>
        <ChevronDown className="h-4 w-4 text-ink-muted shrink-0" />
      </button>

      {open && (
        <div className="absolute z-20 mt-2 w-72 rounded-xl border border-cream-2 bg-white p-3 shadow-xl">
          <p className="text-xs font-semibold text-ink-muted mb-2">Built-in icons</p>
          <div className="max-h-40 overflow-auto grid grid-cols-6 gap-1">
            {ICON_NAMES.map((name) => {
              const active = name === value;
              return (
                <button
                  key={name}
                  type="button"
                  title={name}
                  data-testid={`${tid}-opt-${name}`}
                  onClick={() => {
                    onChange(name);
                    setOpen(false);
                  }}
                  className={`inline-flex h-9 w-9 items-center justify-center rounded-lg transition-colors ${
                    active ? "bg-sage text-white" : "text-ink-soft hover:bg-cream-2"
                  }`}
                >
                  <DynamicIcon name={name} className="h-4 w-4" faSize={16} />
                </button>
              );
            })}
          </div>

          <div className="mt-3 pt-3 border-t border-cream-2">
            <p className="text-xs font-semibold text-ink-muted mb-1">Or use a Font Awesome icon</p>
            <p className="text-[11px] text-ink-muted mb-2">
              Paste a class, e.g. <span className="font-mono">fa-solid fa-brain</span>.{" "}
              <a
                href="https://fontawesome.com/search?o=r&m=free"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sage-dark underline"
              >
                Browse free icons
              </a>
            </p>
            <div className="flex items-center gap-2">
              <span className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-sage/12 text-sage-dark">
                {fa.trim() ? <i className={fa.trim()} style={{ fontSize: 16 }} aria-hidden="true" /> : <span className="text-[10px] text-ink-muted">?</span>}
              </span>
              <input
                data-testid={`${tid}-fa-input`}
                value={fa}
                onChange={(e) => setFa(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); applyFa(); } }}
                placeholder="fa-solid fa-brain"
                className="flex-1 rounded-lg border border-cream-2 px-2 py-1.5 text-sm font-mono outline-none focus:border-sage"
              />
              <button
                type="button"
                data-testid={`${tid}-fa-apply`}
                onClick={applyFa}
                className="rounded-lg bg-sage text-white px-3 py-1.5 text-sm font-semibold hover:bg-sage-dark transition-colors"
              >
                Use
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IconPicker;
